import { useMemo } from "react";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { useRecordContext } from "react-admin";
import type { DocumentRead, TargetSchema } from "@/types";
import { TabContent } from "@/components/common/PageLayout";
import { SectionCard } from "@/components/common/SectionCard";
import { useAppliedFieldMapping } from "../../hooks/useAppliedFieldMapping";
import { useDocumentTransformationData } from "../../hooks/useDocumentTransformationData";
import { useDocumentLoads } from "../result-tab/hooks";
import { parseLoadWarnings } from "../result-tab/parseLoadWarnings";
import { TransformationHeader } from "./TransformationHeader";
import { SchemaMappingCard } from "./SchemaMappingCard";

/**
 * Mapping tab body.
 *
 * Renders:
 *   - TransformationHeader band: template, sub-type, extraction method,
 *     coverage stats, detected-headers summary, and a "Transformation
 *     details" button that opens the detail dialog.
 *   - One SchemaMappingCard per schema in the applied mapping. Each card
 *     wraps an AppliedMappingTable extended with a sample-value column
 *     sourced from the first loaded record of the schema.
 */
export const DocumentMappingTab = () => {
  const document = useRecordContext<DocumentRead>();
  const documentId = document?.id;

  const { schemaMappings, isLoading: isMappingsLoading } = useAppliedFieldMapping();
  const {
    latestTransformation,
    template,
    isLoading: isTransformLoading,
  } = useDocumentTransformationData({
    documentId,
  });
  // Latest load gives us the first entity id per schema so each card can
  // sample a real row. Cached across tabs (react-admin de-dupes by key).
  const { latestLoad } = useDocumentLoads(documentId);

  // First entity id per schema from the latest load, used to seed sample
  // values on each SchemaMappingCard.
  const firstEntityIdBySchema = useMemo(() => {
    const map = new Map<TargetSchema, string>();
    for (const record of latestLoad?.loaded_records ?? []) {
      if (!map.has(record.target_schema)) {
        map.set(record.target_schema, record.entity_id_ref);
      }
    }
    return map;
  }, [latestLoad?.loaded_records]);

  if (!document) {
    return (
      <TabContent>
        <Typography>No document found</Typography>
      </TabContent>
    );
  }

  const isLoading = isMappingsLoading || isTransformLoading;

  if (isLoading) {
    return (
      <TabContent>
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      </TabContent>
    );
  }

  if (!latestTransformation && schemaMappings.length === 0) {
    return (
      <TabContent>
        <SectionCard noDivider>
          <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
            <Typography variant="body1">No field mapping data available for this document</Typography>
          </Box>
        </SectionCard>
      </TabContent>
    );
  }

  return (
    <TabContent>
      <Stack spacing={2}>
        <TransformationHeader
          transformation={latestTransformation}
          template={template}
          schemaMappings={schemaMappings}
          loadedWithoutPaidDate={
            latestLoad ? (parseLoadWarnings(latestLoad.normalization_warnings).noPaidDate?.lines ?? 0) : null
          }
        />
        {schemaMappings.length === 0 ? (
          <SectionCard noDivider>
            <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
              <Typography variant="body2">
                The transformation ran but no field mappings were applied. Open the transformation for details.
              </Typography>
            </Box>
          </SectionCard>
        ) : (
          schemaMappings.map((schemaMapping) => (
            <SchemaMappingCard
              key={schemaMapping.schema}
              schemaMapping={schemaMapping}
              sampleEntityId={firstEntityIdBySchema.get(schemaMapping.schema)}
            />
          ))
        )}
      </Stack>
    </TabContent>
  );
};
